import { Box, Button, Checkbox, Divider, FormControl, FormControlLabel, FormHelperText, FormLabel, Typography } from "@mui/material";
import { ReactElement } from "react";

import { MessageContent, Option, OptionListContent, PlaceAddress, ResultContent } from "../../../components/AddressAutoComplete/AddressAutocomplete.style";
import ArrowPopover from "../../../components/ArrowPopover/ArrowPopover";
import ContentColumnBox from "../../../components/Boxes/ContentColumnBox/ContentColumnBox";
import HorizontalBox from "../../../components/Boxes/HorizontalBox/HorizontalBox";
import { StyledStack } from "../../../components/ChipsGroup/ChipsGroup.style";
import Container from "../../../components/Container/Container";
import RadioSelection from "../../../components/RadioSelection/RadioSelection";
import SingleSelect from "../../../components/SingleSelect/SingleSelect";
import TextField from "../../../components/TextField/TextField";
import { ERROR_REQUIRED_FIELD, ERROR_TAKEN_INVESTOR_NAME, INVESTOR_TYPE_ENTITY, INVESTOR_TYPE_INDIVIDUAL } from "../../../utils/constants/text.constants";
import { Prediction } from "../../../utils/types/address.type";
import { InvestorDetail, MailingAddress } from "../../../utils/types/investor.type";
import { InfoSection } from "../investorList/investorDetailPanel/InvestorDetailPanel.styles";
import { useAddressEffect, useInvestorTypesEffect } from "./InvestorDetails.hooks";

type Props = {
  setIsLoading: Function;
  useInvestorInput: any;
  useCheckbox: any;
  addressValues: MailingAddress;
  taxAddressValues: MailingAddress;
  useAddressInput: Function;
  useTaxAddressInput: Function;
  useTaxAddressInputSelect: Function;
  valuesInvestorDetail: InvestorDetail;
  isUniqueNameError: boolean;
  setUniqueNameError: any;
  setRequiredFieldUpdated: any;
  canUpdateInvestorTin?: boolean;
  canUpdateInvestorName?: boolean;
};

const InvestorDetails: React.FC<Props> = (props: Props): ReactElement => {
  const {
    setIsLoading,
    useInvestorInput,
    useCheckbox,
    addressValues,
    taxAddressValues,
    useAddressInput,
    useTaxAddressInput,
    useTaxAddressInputSelect,
    valuesInvestorDetail,
    isUniqueNameError,
    setUniqueNameError,
    setRequiredFieldUpdated,
    canUpdateInvestorTin,
    canUpdateInvestorName,
  } = props;

  const {
    selectedCountry,
    selectedState,
    street1,
    city,
    zipCode,
    suggestedStateList,
    suggestedCountryList,
    selectedTaxCountry,
    selectedTaxState,
    taxStreet1,
    taxCity,
    taxZipCode,
    suggestedTaxStateList,
    suggestedTaxCountryList,
    showMessageNoMatches,
    addressSuggestions,
    addressSuggestionsAnchorEl,
    taxAddressSuggestions,
    taxAddressSuggestionsAnchorEl,
    hideTaxAddress,
    phone,
    setPhone,
    handleCountryChange,
    handleCityChange,
    handleStateChange,
    handlePostalCodeChange,
    handleOnAddressChange,
    handleCloseArrowPopover,
    handleSelectClick,
    handleOnPhoneChange,
    onTaxAddressCheck
  } = useAddressEffect(
    setIsLoading,
    valuesInvestorDetail,
    addressValues,
    taxAddressValues
  );

  const {
    selectedInvestorType,
    selectedLegalType,
    investorName,
    investorPrefix,
    prefixOptions,
    entityTypeOptions,
    investorTypeList,
    legalTypesList,
    handleOnNameChange,
    handlePrefixChange,
    handleTypeChange,
    handleLegalTypeChange,
    isEmptyNameError,
  } = useInvestorTypesEffect(
    setIsLoading,
    valuesInvestorDetail,
    setUniqueNameError,
    setRequiredFieldUpdated
  );

  return (
    <Container id="container_investor_main" role="main">
      <ContentColumnBox>
        <FormLabel component="legend">This investor is an</FormLabel>
        <RadioSelection
          id={"radio_investor_details_entity"}
          label={"Entity type selection"}
          options={entityTypeOptions}
          {...useInvestorInput("entityType")}
        />
      </ContentColumnBox>

      {canUpdateInvestorName ? (
        <>
          <HorizontalBox noPadding>
            <SingleSelect
              id="select_investor_prefix"
              optionList={prefixOptions}
              label="Prefix"
              value={investorPrefix}
              isShort
              hidden={valuesInvestorDetail.entityType === INVESTOR_TYPE_ENTITY}
              handleOptionSelection={handlePrefixChange}
            />

            <TextField
              size="small"
              id="txt_investor_name"
              label="First Name"
              error={isUniqueNameError}
              required
              {...useInvestorInput("firstName")}
              fullWidth
              hidden={valuesInvestorDetail.entityType === INVESTOR_TYPE_ENTITY}
            />
            <TextField
              size="small"
              id="txt_investor_middle_name"
              label="M.I"
              {...useInvestorInput("middle")}
              isShort
              hidden={valuesInvestorDetail.entityType === INVESTOR_TYPE_ENTITY}
            />
            <TextField
              size="small"
              id="txt_investor_last_name"
              label="Last Name"
              required
              {...useInvestorInput("lastName")}
              fullWidth
              hidden={valuesInvestorDetail.entityType === INVESTOR_TYPE_ENTITY}
            />
          </HorizontalBox>
          {valuesInvestorDetail.entityType === INVESTOR_TYPE_INDIVIDUAL && isUniqueNameError &&
            <HorizontalBox noPadding>
              <FormControl error>
                <FormHelperText id="helper_text_name">
                  {ERROR_TAKEN_INVESTOR_NAME}
                </FormHelperText>
              </FormControl>
            </HorizontalBox>
          }
          <Box id="personal_info_panel">
            <TextField
              size="small"
              id="txt_investor_name_info"
              label="Name"
              required
              hidden={
                valuesInvestorDetail.entityType === INVESTOR_TYPE_INDIVIDUAL
              }
              value={investorName}
              onChange={handleOnNameChange}
              error={isUniqueNameError || isEmptyNameError}
              helperText={
                isUniqueNameError
                  ? ERROR_TAKEN_INVESTOR_NAME
                  : isEmptyNameError
                    ? ERROR_REQUIRED_FIELD
                    : ""
              }
            />
            <TextField
              size="small"
              id="txt_investor_phone"
              label="Phone"
              // {...useInvestorInput("phone")}
              value={phone}
              onChange={handleOnPhoneChange}
            />
          </Box>
        </>
      ) : (
        <HorizontalBox>
          <>
            <InfoSection width="330px">
              <Typography variant="label">Name</Typography>
              <Typography className="field-value">
                {valuesInvestorDetail.entityType === INVESTOR_TYPE_ENTITY
                  ? valuesInvestorDetail?.name
                  : valuesInvestorDetail.fullName}
              </Typography>
            </InfoSection>
            <InfoSection width="330px">
              <Typography variant="label">Phone</Typography>
              <Typography className="field-value">
                {valuesInvestorDetail?.phone ?? "-"}
              </Typography>
            </InfoSection>
          </>
        </HorizontalBox>
      )}

      <Box id="address_panel">
        <TextField
          size="small"
          id="txt_investor_main_address1"
          label="Address line 1"
          onChange={({ target, currentTarget }: any) =>
            handleOnAddressChange({ target, currentTarget }, 'mailing')}
          value={street1}
        />
        <PlaceAddress id="placeid" />
        <ArrowPopover
          id="arrowPopover"
          anchorEl={addressSuggestionsAnchorEl}
          showPopover={Boolean(addressSuggestionsAnchorEl)}
          size="lg"
          handleOnPopoverClose={() => handleCloseArrowPopover('mailing')}
          content={
            <ResultContent>
              <MessageContent>
                {showMessageNoMatches
                  ? `An exact match could not be found. 
                Please select from the options below.`
                  : ""}
              </MessageContent>
              <OptionListContent>
                {addressSuggestions?.map((option: Prediction, index) => {
                  return (
                    <div key={option.place_id}>
                      <StyledStack direction="row">
                        <Option>{option.description || "Not found"}</Option>
                        <Button
                          id={`btn_option_${option.place_id}`}
                          variant="text"
                          disableElevation
                          onClick={() =>
                            handleSelectClick(
                              option.place_id,
                              option.structured_formatting.main_text,
                              'mailing'
                            )
                          }

                          name="Select"
                        >
                          Select
                        </Button>
                      </StyledStack>

                      <Divider />
                    </div>
                  );
                })}
              </OptionListContent>
            </ResultContent>
          }
        />

        <TextField
          size="small"
          id="txt_investor_address_2_mailing_address"
          label="Address line 2"
          {...useAddressInput("street2")}
        />

        <TextField
          size="small"
          id="txt_investor_zip_mailing_address"
          label="Zip/Post-Code"
          value={zipCode}
          onChange={(e: any) => handlePostalCodeChange(e, 'zipCode')}
          inputProps={{ minLength: 5, maxLength: 7, pattern: "[0-9]" }}
        />

        <TextField
          size="small"
          id="txt_investor_city_mailing_address"
          label="City"
          value={city}
          onChange={(e: any) => handleCityChange(e, 'city')}
        />

        <SingleSelect
          id="select_investor_state_mailing_address"
          optionList={suggestedStateList}
          label="State/Province/Region"
          value={selectedState}
          handleOptionSelection={(e: any) => handleStateChange(e, 'state')}
        />

        <SingleSelect
          id="select_investor_country_mailing_address"
          optionList={suggestedCountryList}
          label="Country"
          value={selectedCountry}
          handleOptionSelection={(e: any) => handleCountryChange(e, 'country')}
        />
        <ContentColumnBox>
          <FormControlLabel
            control={<Checkbox id="check_Tax_Address" />}
            id="check_investor_tax_address"
            label="Tax Address"
            checked={hideTaxAddress}
            value={hideTaxAddress}
            onClick={onTaxAddressCheck}
          />
          <FormHelperText>
            Check if same as primary address above
          </FormHelperText>
        </ContentColumnBox>
      </Box>

      {/* TAX ADDRESS */}
      <Box id="tax_address_panel" hidden={hideTaxAddress}>
        <TextField
          size="small"
          id="txt_investor_tax_street1"
          label="Address line 1"
          onChange={({ target, currentTarget }: any) =>
            handleOnAddressChange({ target, currentTarget }, 'tax')}
          value={taxStreet1}
        />
        <PlaceAddress id="taxplaceid" />
        <ArrowPopover
          id="arrowPopover"
          anchorEl={taxAddressSuggestionsAnchorEl}
          showPopover={Boolean(taxAddressSuggestionsAnchorEl)}
          size="lg"
          handleOnPopoverClose={() => handleCloseArrowPopover('tax')}
          content={
            <ResultContent>
              <MessageContent>
                {showMessageNoMatches
                  ? `An exact match could not be found. 
                Please select from the options below.`
                  : ""}
              </MessageContent>
              <OptionListContent>
                {taxAddressSuggestions?.map((option: Prediction, index) => {
                  return (
                    <div key={option.place_id}>
                      <StyledStack direction="row">
                        <Option>{option.description || "Not found"}</Option>
                        <Button
                          id={`btn_option_${option.place_id}`}
                          variant="text"
                          disableElevation
                          onClick={() =>
                            handleSelectClick(
                              option.place_id,
                              option.structured_formatting.main_text,
                              'tax'
                            )
                          }
                          name="Select"
                        >
                          Select
                        </Button>
                      </StyledStack>

                      <Divider />
                    </div>
                  );
                })}
              </OptionListContent>
            </ResultContent>
          }
        />
        <TextField
          size="small"
          id="txt_investor_street2_tax_address"
          label="Address line 2"
          {...useTaxAddressInput("street2")}
        />

        <TextField
          size="small"
          id="txt_investor_zip_tax_address"
          label="Zip/Post-Code"
          inputProps={{ minLength: 5, maxLength: 7, pattern: "[0-9]" }}
          value={taxZipCode}
          onChange={(e: any) => handlePostalCodeChange(e, 'taxZipCode')}
        />

        <TextField
          size="small"
          id="txt_investor_city_tax_address"
          label="City"
          value={taxCity}
          onChange={(e: any) => handleCityChange(e, 'taxCity')}
        />

        <SingleSelect
          id="select_investor_state_tax_address"
          optionList={suggestedTaxStateList}
          label="State/Province/Region"
          value={selectedTaxState}
          handleOptionSelection={(e: any) => handleStateChange(e, 'taxState')}
        />

        <SingleSelect
          id="select_investor_country_tax_address"
          optionList={suggestedTaxCountryList}
          label="Country"
          value={selectedTaxCountry}
          handleOptionSelection={(e: any) => handleCountryChange(e, 'taxCountry')}
        />
      </Box>

      <br />
      <Box id="other_info_panel">
        {canUpdateInvestorTin ? (
          <>
            <TextField
              size="small"
              id="txt_investor_tin"
              label="TIN"
              {...useInvestorInput("taxIdentificationNumber")}
            />

            <SingleSelect
              id="select_investor_type"
              optionList={investorTypeList}
              label="Investor Type"
              value={selectedInvestorType}
              required
              handleOptionSelection={handleTypeChange}
            />
          </>
        ) : (
          <HorizontalBox>
            <InfoSection width="330px">
              <Typography variant="subInfo">TIN</Typography>
              <Typography className="field-value">
                {valuesInvestorDetail?.taxIdentificationNumber
                  ? valuesInvestorDetail?.taxIdentificationNumber
                  : "-"}
              </Typography>
            </InfoSection>
            <SingleSelect
              id="select_investor_type"
              optionList={investorTypeList}
              label="Investor Type"
              value={selectedInvestorType}
              required
              handleOptionSelection={handleTypeChange}
            />
          </HorizontalBox>
        )}
        <SingleSelect
          id="select_investor_legal_type"
          optionList={legalTypesList}
          value={selectedLegalType}
          handleOptionSelection={handleLegalTypeChange}
          label="Legal Type"
        />

        <ContentColumnBox>
          <FormControlLabel
            control={<Checkbox />}
            id="check_investor_disregarded"
            label="Disregarded Entity"
            {...useCheckbox("disregardedEntity")}
          />
          <FormHelperText>Check if yes</FormHelperText>
        </ContentColumnBox>

        <TextField
          size="small"
          id="txt_owner_name"
          label="Owner Name"
          hidden={!valuesInvestorDetail.disregardedEntity}
          required={valuesInvestorDetail.disregardedEntity}
          {...useInvestorInput("ownerName")}
        />

        <TextField
          size="small"
          id="txt_owner_ssn"
          label="Owner SSN"
          hidden={!valuesInvestorDetail.disregardedEntity}
          required={valuesInvestorDetail.disregardedEntity}
          {...useInvestorInput("ownerSSN")}
        />
      </Box>
    </Container>
  );
};

export default InvestorDetails;
